import React from 'react';
import {
  WithStyles,
  WithTheme,
  withStyles,
  withTheme,
  createStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import SurveyItem from '../../util/SurveyItem';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import {
  withSurveyContext,
  withIsReadOnly,
  WithIsReadOnlyInjectedProps,
  WithSurveyContextInjectedProps,
} from '../Contexts';
import SurveyField from '../SurveyField';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import withValidation, {
  WithValidationInjectedProps,
} from '../hoc/withValidation';
import ChoiceOptions from '../../util/ChoiceOptions';
import { FieldProps } from 'formik';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { RadioGroup, Radio } from '@lifeomic/chroma-react/components/Radio';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { getTestProps } from '../../../../util/testProps';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';

export const testIds = {
  radioOption: (linkId: string, value: string) =>
    getSurveyItemTestId(linkId, value),
  clearButton: (linkId: string) =>
    getSurveyItemTestId(linkId, 'radio-clear-button'),
  errorMessage: (linkId: string) =>
    getSurveyItemTestId(linkId, 'radio-button-group-error'),
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(4),
      width: '100%', // TODO: this is a placeholder, should be responsive
    },
    radioGroupContainer: {
      marginTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    errorText: {
      color: theme.palette.error.main,
    },
    clearButton: {
      marginTop: theme.spacing(2),
    },
    radioGroupTextArea: {
      marginTop: theme.spacing(2),
    },
    // Enlarge the clickable row for tablet use; the chroma Radio root div
    // handles clicks, so padding grows the tap target without moving the
    // visual circle much.
    radioOption: {
      padding: theme.spacing(0.75, 1, 0.75, 0),
    },
  });

interface FieldComponentOwnProps {
  surveyItem: SurveyItem;
  shouldShowError: boolean;
  error: string;
  isReadOnly: boolean;
  choiceOptions: ChoiceOptions;
  isOpenChoice: boolean;
  intl: IntlShape;
}

const messages = defineMessages({
  other: {
    id: 'radioGroup.other',
    defaultMessage: 'Other',
  },
  placeholder: {
    id: 'radioGroup.textarea.placeholder',
    defaultMessage: 'Please specify',
  },
  ariaLabel: {
    id: 'radioGroup.textarea.ariaLabel',
    defaultMessage: 'other text area',
  },
});

type FieldComponentProps = FieldComponentOwnProps &
  FieldProps<any> &
  WithStyles<typeof styles>;

class FieldComponent extends React.PureComponent<FieldComponentProps> {
  state = { isOpenChoiceFocused: false, textFieldValue: '' };

  constructor(props: FieldComponentProps) {
    super(props);

    // Load answer into state if it is an "Other(open-choice)" answer
    if (
      props.isOpenChoice &&
      typeof props.field?.value === 'string' &&
      props.field?.value?.startsWith('Other(open-choice): ')
    ) {
      const normalizedValue = props.field.value.replace(
        'Other(open-choice): ',
        '',
      );
      this.state = {
        isOpenChoiceFocused: true,
        textFieldValue: normalizedValue,
      };
    }
  }
  // The field value may be represented as a complex object in form state, but must be represented as a simple value for the control
  wrapValue = (value: any) => {
    return this.props.choiceOptions.formToResponseValue(value);
  };

  unwrapValue = (responseValue: any) => {
    return this.props.choiceOptions.responseToFormValue(responseValue);
  };

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { form, field } = this.props;

    const wrappedValue = this.wrapValue(event.target.value);
    form.setFieldValue(field.name, wrappedValue);
  };

  handleClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    // Blur of open choice input
    this.setState({ isOpenChoiceFocused: false });
    const clickTargetValue = (event?.target as any)?.value;

    // only handle toggling off
    const selectedValue = this.unwrapValue(this.props.field?.value);
    if (!!clickTargetValue && isEqual(selectedValue, selectedValue)) {
      this.clearValue();
    }
  };

  clearValue = () => {
    const { form, field } = this.props;

    form.setFieldValue(field.name, null);
  };

  handleChangedOther = (e: any) => {
    if (e.target.checked) {
      this.setState({ isOpenChoiceFocused: true });
    }
  };

  handleSyncWithForm = () => {
    const { form, field } = this.props;
    if (this.state.textFieldValue && this.state.textFieldValue !== '') {
      form.setFieldValue(
        field.name,
        `Other(open-choice): ${this.state.textFieldValue}`,
      );
    } else {
      form.setFieldValue(field.name, null);
    }
  };

  handleChangeOpenChoiceText = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    if (event.target.value && event.target.value !== '') {
      this.setState({ textFieldValue: event.target.value });
    } else {
      this.setState({ textFieldValue: null });
    }
  };

  render() {
    const {
      field: { value, name },
      surveyItem,
      classes,
      shouldShowError,
      error,
      choiceOptions,
      isReadOnly,
      isOpenChoice,
      intl,
    } = this.props;

    const selectedValue = this.unwrapValue(value);
    const radioGroupValue = isNil(selectedValue) ? '' : selectedValue;

    return (
      <div className={classes.root}>
        <RadioGroup
          key={`${name}-${selectedValue}`} // TODO: remove this when radio group is completely controlled
          title={surveyItem.label}
          value={radioGroupValue}
          onChange={this.handleChange}
          name={name}
        >
          {choiceOptions.options.map((option, index) => (
            <Radio
              {...getTestProps(
                testIds.radioOption(name, this.unwrapValue(option.value)),
              )}
              className={classes.radioOption}
              onClick={this.handleClick}
              key={index}
              value={this.unwrapValue(option.value)}
              label={option.display}
              disabled={isReadOnly}
            />
          ))}
          {
            // Render the text area if the "Other" option is selected
            !isOpenChoice ? null : (
              <div>
                <Radio
                  onChange={this.handleChangedOther}
                  label={intl.formatMessage(messages.other)}
                  disabled={isReadOnly}
                  checked={this.state.isOpenChoiceFocused}
                  {...getTestProps(
                    getSurveyItemTestId(
                      surveyItem.link,
                      `radiobuttonOption-other`,
                    ),
                  )}
                />
                <TextArea
                  readOnly={isReadOnly}
                  className={classes.radioGroupTextArea}
                  hidden={!this.state.isOpenChoiceFocused}
                  placeholder={intl.formatMessage(messages.placeholder)}
                  onBlur={this.handleSyncWithForm}
                  onChange={e => this.handleChangeOpenChoiceText(e)}
                  aria-label={intl.formatMessage(messages.ariaLabel)}
                  value={this.state.textFieldValue ?? ''}
                  {...getTestProps(
                    getSurveyItemTestId(
                      surveyItem.link,
                      `radiobuttonOption-textarea`,
                    ),
                  )}
                />
              </div>
            )
          }
        </RadioGroup>
        {shouldShowError && (
          <Text
            {...getTestProps(testIds.errorMessage(name))}
            size="caption"
            className={classes.errorText}
          >
            {error}
          </Text>
        )}
      </div>
    );
  }
}

interface RadioButtonGroupOwnProps {
  surveyItem: SurveyItem;
  intl: IntlShape;
}

export type RadioButtonGroupProps = RadioButtonGroupOwnProps &
  WithStyles<typeof styles> &
  WithTheme &
  WithCheckForEnablementInjectedProps &
  WithIsReadOnlyInjectedProps &
  WithSurveyContextInjectedProps &
  WithValidationInjectedProps;

class RadioButtonGroup extends React.PureComponent<RadioButtonGroupProps> {
  choiceOptions: ChoiceOptions;

  constructor(props: RadioButtonGroupProps) {
    super(props);

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.choiceOptions = props.survey.getChoiceOptionsForItem(
      props.surveyItem.link,
    );
  }

  render() {
    const {
      surveyItem,
      isEnabled,
      shouldShowError,
      error,
      validate,
      classes,
      isReadOnly,
      theme,
    } = this.props;

    if (!isEnabled) return null;

    // Honor the indentation modifierExtension. Radio groups are not wrapped
    // in SurveyItemContainer (wrapping would change vertical margins on every
    // survey), so mirror its marginLeft arithmetic here for indented items
    // only, keeping them aligned with indented text/choice/boolean fields.
    // indentationCount can be NaN when the extension has no valueInteger
    const indentationCount = surveyItem.isIndented
      ? (surveyItem.hasDenseMargins ? 3 : 0) +
        (surveyItem.indentationCount || 0)
      : 0;
    const marginLeft = indentationCount
      ? theme.spacing(indentationCount)
      : undefined;

    return (
      <div style={marginLeft ? { marginLeft } : undefined}>
        <SurveyField
          validate={validate}
          responseAnswerType={surveyItem.responseValueType}
          name={surveyItem.link}
          component={FieldComponent}
          surveyItem={surveyItem}
          isEnabled={isEnabled}
          isReadOnly={isReadOnly}
          error={error}
          isOpenChoice={surveyItem.type === 'open-choice'}
          itemType={surveyItem.type}
          shouldShowError={shouldShowError}
          choiceOptions={this.choiceOptions}
          classes={classes}
          intl={this.props.intl}
        />
      </div>
    );
  }
}

export default withTheme(
  withStyles(styles)(
    withIsReadOnly(
      withCheckForEnablement(
        withValidation(withSurveyContext(injectIntl(RadioButtonGroup))),
      ),
    ),
  ),
);
